import { isNativeAppWebview } from 'core/utils/utils';
import { KeyboardEvent, MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

let globalSelectCounter = 0;

type StyleFn = (base: Record<string, unknown>, state?: Record<string, unknown>) => Record<string, unknown>;

type SharedStyles = {
  menu: StyleFn;
  menuPortal: StyleFn;
  control: StyleFn;
  option: StyleFn;
};

type UseSelectCustomizableParams = {
  inputId?: string;
  name?: string;
  isSearchable?: boolean;
  error?: string;
  noOptionsMessage?: string;
  t: (key: string) => string;
};

type UseSelectCustomizableReturn = {
  resolvedInputId: string;
  labelId: string;
  listboxId: string;
  selectRef: MutableRefObject<unknown>;
  focusedOptionId: string | null;
  onOptionFocusChange: (id: string) => void;
  handleKeyDown: (event: KeyboardEvent) => void;
  handleMenuOpen: () => void;
  handleMenuClose: () => void;
  finalIsSearchable: boolean | undefined;
  finalMenuPlacement: 'top' | 'auto';
  getNoOptionsMessage: () => string;
  sharedStyles: SharedStyles;
};

type SelectInstance = {
  openMenu?: (position: string) => void;
};

type SelectRefValue = {
  select?: SelectInstance & { select?: SelectInstance };
};

function isMobileDevice(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPad = /ipad/.test(userAgent) || (navigator.maxTouchPoints > 1 && /macintosh/.test(userAgent));
  const isMobile = /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  return isIPad || isMobile || isNativeAppWebview();
}

function getControlBorderStyles(
  isFocused: boolean,
  hasValue: boolean,
  isSearchable: boolean | undefined,
  hasError: boolean
): Record<string, unknown> {
  const hoverBorderColor = hasError
    ? 'var(--select-customizable-border-error)'
    : 'var(--select-customizable-border-active)';

  if (hasError) {
    return {
      border: '2px solid var(--select-customizable-border-error)',
      '&:hover': { borderColor: hoverBorderColor },
    };
  }
  const isActive = isFocused || hasValue || isSearchable;
  if (isActive) {
    return {
      border: '2px solid var(--select-customizable-border-active)',
      '&:hover': { borderColor: hoverBorderColor },
    };
  }
  return {
    border: '1px solid var(--select-customizable-border-default)',
    '&:hover': {
      borderColor: hoverBorderColor,
      boxShadow: `0 0 0 1px ${hoverBorderColor}`,
    },
  };
}

function getInnerSelectInstance(ref: MutableRefObject<unknown>): SelectInstance | null {
  if (!ref.current) return null;
  // ref.current = StateManager instance
  // ref.current.select = inner Select (regular) OR Creatable (creatable)
  // ref.current.select.select = inner Select (creatable only)
  const { select: inner } = ref.current as SelectRefValue;
  if (!inner) return null;
  return inner.select ?? inner;
}

export default function useSelectCustomizable({
  inputId,
  name,
  isSearchable,
  error,
  noOptionsMessage,
  t,
}: UseSelectCustomizableParams): UseSelectCustomizableReturn {
  const instanceIdRef = useRef(`select-accessible-${++globalSelectCounter}`);
  const resolvedInputId = inputId || name || instanceIdRef.current;
  const labelId = `${resolvedInputId}-label`;
  const listboxId = `${resolvedInputId}-listbox`;

  const selectRef = useRef<unknown>(null);
  const menuIsOpenRef = useRef(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);

  const isOnMobileDevice = useMemo(isMobileDevice, []);
  const finalIsSearchable = isOnMobileDevice ? false : isSearchable;
  const finalMenuPlacement: 'top' | 'auto' = isOnMobileDevice ? 'top' : 'auto';

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter' && !menuIsOpenRef.current) {
      event.preventDefault();
      const instance = getInnerSelectInstance(selectRef);
      if (instance && typeof instance.openMenu === 'function') {
        instance.openMenu('first');
      }
    }
  }, []);

  const handleMenuOpen = useCallback(() => {
    menuIsOpenRef.current = true;
  }, []);

  const handleMenuClose = useCallback(() => {
    menuIsOpenRef.current = false;
    setFocusedOptionId(null);
  }, []);

  const onOptionFocusChange = useCallback((id: string) => {
    setFocusedOptionId(id || null);
  }, []);

  const getNoOptionsMessage = useCallback(() => {
    return noOptionsMessage ?? t('placeholder:No options');
  }, [noOptionsMessage, t]);

  const sharedStyles = useMemo<SharedStyles>(
    () => ({
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (provided, state = {}) => ({
        ...provided,
        boxShadow: undefined,
        cursor: 'pointer',
        borderRadius: 'var(--select-customizable-border-radius)',
        transition: 'border-color 0.4s',
        ...getControlBorderStyles(
          Boolean(state.isFocused),
          Boolean(state.hasValue),
          finalIsSearchable,
          !!error
        ),
      }),
      option: (provided) => ({
        ...provided,
        backgroundColor: 'transparent',
        color: 'inherit',
      }),
    }),
    [error, finalIsSearchable]
  );

  return {
    resolvedInputId,
    labelId,
    listboxId,
    selectRef,
    focusedOptionId,
    onOptionFocusChange,
    handleKeyDown,
    handleMenuOpen,
    handleMenuClose,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    sharedStyles,
  };
}
